import { useAppSelector } from "app/store";
import { Form, Formik, FormikHelpers } from "formik";
import useRetryTimeCounter from "hooks/useRetryTimeCounter";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { smsPasscodeActions } from "./smsPasscode.slice";
import * as Yup from "yup";
import Card from "components/ui/Card/Card";
import CardFooter from "../components/CardFooter";
import { H3, H6 } from "components/ui/Headings/Headings";
import InputNumberFormGroup from "components/forms/InputNumberFormGroup";
import Button from "components/ui/Button/Button";
import Modal from "components/ui/Modal/Modal";
import ForgotPasswordTimedOut from "../components/ForgotPasswordTimedOut";
import useFormValidations from "hooks/useFormValidations";
import Alert from "components/ui/Alert/Alert";
import Content from "components/layout/Content";
import { mapPhoneNumberFormGroupValueToString } from "utils/mappers";
import useEventLogger from "hooks/useEventLogger";

export interface ForgotPasswordSMSPasscodeFormValues {
  otp: number | null;
}

const SMSPasscode: React.FC = () => {
  const { formValues: previousStepFormValues } = useAppSelector(
    (state) => state.forgotPassword.home
  );

  const { otpValidation } = useFormValidations();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    remainingTimeCountingInProgress,
    secondsRemainingToRetry,
    resetTimer,
  } = useRetryTimeCounter();
  const [passcodeNotMatchModalIsOpen, setPasscodeNotMatchModalIsOpen] =
    useState(false);

  const { trackAction } = useEventLogger("ForgotPasswordSubmitOTP");

  const handleSubmit: (
    values: ForgotPasswordSMSPasscodeFormValues,
    formikHelpers: FormikHelpers<ForgotPasswordSMSPasscodeFormValues>
  ) => void | Promise<any> = async (values, { setSubmitting }) => {
    dispatch(smsPasscodeActions.setFormValues(values));

    trackAction({});

    try {
      await dispatch(smsPasscodeActions.checkPasscode(values.otp + ""));

      history.push(`${history.location.pathname}/reset-password`);
    } catch (error) {
      console.error(error);
      setPasscodeNotMatchModalIsOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendSMSClick = () => {
    resetTimer();

    dispatch(smsPasscodeActions.resendSMS());
  };

  const handlePasscodeNotMatchModalOk = () => {
    handleResendSMSClick();

    setPasscodeNotMatchModalIsOpen(false);
  };

  const handlePasscodeNotMatchModalCancel = () => {
    setPasscodeNotMatchModalIsOpen(false);
  };

  useEffect(() => {
    dispatch(smsPasscodeActions.reset());
  }, [dispatch]);

  return previousStepFormValues ? (
    <>
      <Content>
        <Formik<ForgotPasswordSMSPasscodeFormValues>
          initialValues={{
            otp: null,
          }}
          validationSchema={Yup.object({
            otp: otpValidation,
          })}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {(formikProps) => (
            <Card
              footer={
                <CardFooter<ForgotPasswordSMSPasscodeFormValues>
                  formikProps={formikProps}
                  cancelButtonText={t(
                    "forgotPassword.smsPasscode.cancelButtonText"
                  )}
                  nextButtonText={t(
                    "forgotPassword.smsPasscode.nextButtonText"
                  )}
                />
              }
              isDense
            >
              <H6>{t("forgotPassword.smsPasscode.subtitle")}</H6>
              <H3>{t("forgotPassword.smsPasscode.title")}</H3>

              <Alert type="success">
                {t("forgotPassword.smsPasscode.smsSentToNumber")}
                <div className="font-bold">
                  {mapPhoneNumberFormGroupValueToString(
                    previousStepFormValues.phoneNumber
                  )}
                </div>
              </Alert>

              <p className="my-4">
                {t("forgotPassword.smsPasscode.description")}
              </p>

              <Form noValidate>
                <div className="divide-y divide-main">
                  <div>
                    <InputNumberFormGroup
                      name="otp"
                      label={t("forgotPassword.smsPasscode.otp")}
                      placeholder={t(
                        "forgotPassword.smsPasscode.otpPlaceholder"
                      )}
                      required
                      thousandSeparator={false}
                      format="######"
                    />
                  </div>
                  <div className="pt-4">
                    <p className="text-center w-44 max-w-full mx-auto mb-4">
                      {t("forgotPassword.smsPasscode.didntReceiveSMS")}
                    </p>
                    <div className="text-center">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleResendSMSClick}
                        disabled={remainingTimeCountingInProgress}
                      >
                        {t("forgotPassword.smsPasscode.resendSMSButtonText")}{" "}
                        {remainingTimeCountingInProgress && (
                          <>({secondsRemainingToRetry})</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            </Card>
          )}
        </Formik>
      </Content>
      <Modal
        isOpen={passcodeNotMatchModalIsOpen}
        title={t("forgotPassword.smsPasscode.passcodeNotMatchModal.title")}
        description={t(
          "forgotPassword.smsPasscode.passcodeNotMatchModal.description"
        )}
        okText={t(
          "forgotPassword.smsPasscode.passcodeNotMatchModal.resendSMSButtonText"
        )}
        cancelText={t(
          "forgotPassword.smsPasscode.passcodeNotMatchModal.cancelButtonText"
        )}
        type="alert"
        alertType="error"
        onOk={handlePasscodeNotMatchModalOk}
        onCancel={handlePasscodeNotMatchModalCancel}
        showCancelButton
        size="large"
      />
    </>
  ) : (
    <ForgotPasswordTimedOut />
  );
};

export default SMSPasscode;
